import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { IDeliveryLimitations } from '../../../models/parcelDimensions';
import { Measure } from '../../../utils/parcel-measurements.ts/parcel-measurements-utils';
import { getLengthConfig, getWeightConfig } from './dimensionsConfig';
import { sideValidatorMax, sideValidatorMin } from './helpers';

const MIN_SIDE = 0.1;
const yupCommon = Yup.number()
  .transform((value, originalValue) => {
    if (
      originalValue === undefined ||
      originalValue === null ||
      originalValue === ''
    ) {
      return undefined;
    }

    return Number.isNaN(value) ? undefined : value;
  })
  .required('this_field_cannot_be_empty');

const initialValidationSchema = Yup.object({
  weight: yupCommon,
  size_x: yupCommon,
  size_y: yupCommon,
  size_z: yupCommon,
});

interface UseDimensionsValidationSchemaProps {
  deliveryLimitations?: IDeliveryLimitations;
  measures: Measure;
}

const useDimensionsValidationSchema = ({
  deliveryLimitations,
  measures,
}: UseDimensionsValidationSchemaProps) => {
  const { t } = useTranslation();
  const { translation: lengthUnit } = getLengthConfig(measures);
  const { translation: weightUnit } = getWeightConfig(measures);

  const getSizeValidationText = (
    size: 'X' | 'Y' | 'Z',
    type: 'max' | 'min'
  ) => {
    const translations: Record<
      'max' | 'min',
      Record<'X' | 'Y' | 'Z', string>
    > = {
      min: {
        X: 'create_parcel_length_short',
        Y: 'create_parcel_width_short',
        Z: 'create_parcel_height_short',
      },
      max: {
        X: 'create_parcel_length_long',
        Y: 'create_parcel_width_long',
        Z: 'create_parcel_height_long',
      },
    };

    const limit = type === 'max' ? deliveryLimitations?.maxSide : MIN_SIDE;

    return `${t(translations[type][size])} ${limit} ${lengthUnit}`;
  };

  const getWeightValidationText = (type: 'max' | 'min') => {
    const translations = {
      min: 'create_parcel_weight_light',
      max: 'create_parcel_weight_large',
    };

    return `${t(translations[type])} ${deliveryLimitations?.[`${type}Weight`]} ${weightUnit}`;
  };

  const validationSchema = Yup.object({
    weight: yupCommon
      .test('less-than', getWeightValidationText('max'), (value) => {
        if (value === undefined) return true;
        if (!deliveryLimitations?.maxWeight) return true;
        return Number(value) <= deliveryLimitations.maxWeight;
      })
      .test('more-than', getWeightValidationText('min'), (value) => {
        if (value === undefined) return true;
        if (!deliveryLimitations?.minWeight) return true;
        return Number(value) >= deliveryLimitations.minWeight;
      }),
    size_x: yupCommon
      .test(
        'less-than',
        getSizeValidationText('X', 'max'),
        sideValidatorMax(deliveryLimitations?.maxSide)
      )
      .test(
        'more-than',
        getSizeValidationText('X', 'min'),
        sideValidatorMin(MIN_SIDE)
      ),
    size_y: yupCommon
      .test(
        'less-than',
        getSizeValidationText('Y', 'max'),
        sideValidatorMax(deliveryLimitations?.maxSide)
      )
      .test(
        'more-than',
        getSizeValidationText('Y', 'min'),
        sideValidatorMin(MIN_SIDE)
      ),
    size_z: yupCommon
      .test(
        'less-than',
        getSizeValidationText('Z', 'max'),
        sideValidatorMax(deliveryLimitations?.maxSide)
      )
      .test(
        'more-than',
        getSizeValidationText('Z', 'min'),
        sideValidatorMin(MIN_SIDE)
      ),
  });

  return !deliveryLimitations ? initialValidationSchema : validationSchema;
};

export default useDimensionsValidationSchema;
